import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

export interface FileUploadProps {
  id: string;
  label?: string;
  accept?: string;
  maxSizeMb?: number; // blocks when exceeded
  description?: string;
  value?: File | null;
  previewUrl?: string; // external preview (optional)
  onChange?: (file: File | null) => void;
  showPreview?: boolean;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  id,
  label,
  accept,
  maxSizeMb = 5,
  description,
  value = null,
  previewUrl,
  onChange,
  showPreview = true,
}) => {
  const { theme } = useTheme();
  const [isDragging, setIsDragging] = useState(false);
  const [localFile, setLocalFile] = useState<File | null>(value);
  const [localPreview, setLocalPreview] = useState<string>('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setLocalFile(value || null);
  }, [value]);

  useEffect(() => {
    if (previewUrl) {
      setLocalPreview(previewUrl);
      return;
    }
    if (localFile) {
      // Use URL for better perf than FileReader for large images
      const url = URL.createObjectURL(localFile);
      setLocalPreview(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setLocalPreview('');
    }
  }, [localFile, previewUrl]);

  const handleFiles = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (maxSizeMb && file.size > maxSizeMb * 1024 * 1024) {
      alert(`Arquivo excede ${maxSizeMb}MB`);
      return;
    }
    setLocalFile(file);
    onChange?.(file);
  }, [maxSizeMb, onChange]);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const clearSelection = () => {
    setLocalFile(null);
    onChange?.(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const borderColor = isDragging ? 'var(--primary-blue-light)' : 'var(--border-color)';
  const bg = theme === 'dark' ? 'var(--bg-secondary)' : '#ffffff';
  const textColor = 'var(--text-primary)';
  const subText = 'var(--text-secondary)';

  return (
    <div>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium mb-2" style={{ color: textColor }}>
          {label}
        </label>
      )}

      <div
        role="button"
        tabIndex={0}
        aria-label={label || 'Uploader de arquivo'}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
        }}
        onClick={() => inputRef.current?.click()}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        className="w-full rounded-lg border-2 border-dashed transition-colors p-4 cursor-pointer"
        style={{ borderColor, backgroundColor: bg }}
      >
        <div className="flex items-center gap-4">
          <div
            className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
            style={{ backgroundColor: theme === 'dark' ? '#2a2a2f' : '#f3f4f6', color: subText }}
          >
            📁
          </div>

          <div className="flex-1">
            <p className="text-sm" style={{ color: textColor }}>
              {localFile ? (
                <span className="font-medium">{localFile.name}</span>
              ) : (
                <>
                  Arraste e solte o arquivo aqui ou
                  <span className="mx-1 underline"> clique para escolher</span>
                </>
              )}
            </p>
            <p className="text-xs mt-1" style={{ color: subText }}>
              {description || `Tamanho máximo ${maxSizeMb}MB${accept ? ` • Tipos: ${accept}` : ''}`}
            </p>
          </div>

          {localFile && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); clearSelection(); }}
              className="inline-flex items-center justify-center h-6 w-6 p-0 rounded-lg transition-all duration-200 ease-in-out focus:outline-none"
              title="Remover"
              aria-label="Remover arquivo"
              style={{
                background: 'rgba(229, 70, 102, 0.15)',
                color: (theme === 'dark' ? '#FF949D' : '#CA244D')
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(229, 70, 102, 0.2)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(229, 70, 102, 0.15)'; }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
                <path d="M3 6h18" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6" />
                <path d="M14 11v6" />
                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        onChange={(e) => handleFiles(e.target.files)}
        className="hidden"
      />

      {showPreview && localPreview && (
        <div className="mt-3">
          <div className="flex items-center gap-3">
            {/* Only attempt to render as image if it seems to be an image */}
            {localFile?.type.startsWith('image/') || !localFile ? (
              <img
                src={localPreview}
                alt="Preview"
                className="rounded"
                style={{ width: 64, height: 64, objectFit: 'contain', border: '1px solid var(--border-color)' }}
              />
            ) : (
              <div className="text-xs" style={{ color: subText }}>
                Preview indisponível para este tipo de arquivo
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;


